import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiQuery } from '../hooks/useApi';
import { useFiltroMaquinasUsuario } from '../hooks/useFiltroMaquinasUsuario';
import { CrudTable, CrudForm } from '../components/admin/crud';
import { MaquinasService } from '../services';
import type { Maquina, CreateMaquinaRequest } from '../types';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const MaquinasView: React.FC = () => {
  const { t } = useTranslation();
  const { usuario, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { maquinasPermitidas, tieneAccesoCompleto, isLoading: isLoadingFiltro } = useFiltroMaquinasUsuario();

  // ===============================
  // CONFIGURACIÓN DE COLUMNAS
  // ===============================

  const columns = useMemo(() => [
    {
      key: 'codigo',
      title: t('common.code'),
      width: '120px',
    },
    {
      key: 'marca_modelo',
      title: 'Marca/Modelo',
      width: '200px',
      render: (_: unknown, row: Maquina) => (
        <div>
          <div className="font-medium">{row.marca} {row.modelo}</div>
          <div className="text-sm text-gray-500">{row.tipo}</div>
        </div>
      ),
    },
    {
      key: 'anio',
      title: 'Año',
      width: '80px',
    },
    {
      key: 'numero_serie',
      title: 'N° Serie',
      width: '130px',
    },
    {
      key: 'ubicacion_base',
      title: 'Ubicación',
      width: '150px',
      render: (ubicacion: string) => ubicacion || '-',
    },
    {
      key: 'estado',
      title: t('common.status'),
      width: '100px',
      render: (estado: string) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          estado === 'activo'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {estado === 'activo' ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FILTROS
  // ===============================

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('common.search'),
      type: 'text' as const,
      placeholder: 'Código, marca, modelo...',
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select' as const,
      options: [
        { value: 'HARVESTER', label: 'Harvester' },
        { value: 'FORWARDER', label: 'Forwarder' },
        { value: 'FELLER_BUNCHER', label: 'Feller Buncher' },
        { value: 'SKIDDER', label: 'Skidder' },
        { value: 'EXCAVADORA', label: 'Excavadora' },
        { value: 'BULLDOZER', label: 'Bulldozer' },
        { value: 'GRUA', label: 'Grúa' },
        { value: 'CAMION', label: 'Camión' },
        { value: 'OTRO', label: 'Otro' },
      ],
    },
    {
      key: 'es_activa',
      label: t('common.status'),
      type: 'select' as const,
      options: [
        { value: 'activo', label: t('common.active') },
        { value: 'inactivo', label: t('common.inactive') },
      ],
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DE FORMULARIO
  // ===============================

  const formFields = useMemo(() => [
    {
      name: 'codigo',
      label: t('common.code'),
      type: 'text' as const,
      placeholder: 'Código único de la máquina',
      required: true,
    },
    {
      name: 'tipo',
      label: 'Tipo',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'HARVESTER', label: 'Harvester' },
        { value: 'FORWARDER', label: 'Forwarder' },
        { value: 'FELLER_BUNCHER', label: 'Feller Buncher' },
        { value: 'SKIDDER', label: 'Skidder' },
        { value: 'EXCAVADORA', label: 'Excavadora' },
        { value: 'BULLDOZER', label: 'Bulldozer' },
        { value: 'GRUA', label: 'Grúa' },
        { value: 'CAMION', label: 'Camión' },
        { value: 'OTRO', label: 'Otro' },
      ],
    },
    {
      name: 'marca',
      label: 'Marca',
      type: 'text' as const,
      placeholder: 'Caterpillar, John Deere, etc.',
      required: true,
    },
    {
      name: 'modelo',
      label: 'Modelo',
      type: 'text' as const,
      placeholder: 'Modelo de la máquina',
      required: true,
    },
    {
      name: 'anio',
      label: 'Año',
      type: 'number' as const,
      placeholder: '2020',
      min: 1990,
      max: new Date().getFullYear() + 1,
      required: true,
    },
    {
      name: 'numero_serie',
      label: 'Número de Serie',
      type: 'text' as const,
      placeholder: 'Número de serie único',
      required: true,
    },
    {
      name: 'descripcion',
      label: t('common.description'),
      type: 'textarea' as const,
      placeholder: 'Descripción adicional de la máquina',
      rows: 3,
    },
    {
      name: 'ubicacion_base',
      label: 'Ubicación Base',
      type: 'text' as const,
      placeholder: 'Ubicación principal de operación',
    },
    {
      name: 'estado',
      label: t('common.status'),
      type: 'select' as const,
      required: true,
      options: [
        { value: 'activo', label: t('common.active') },
        { value: 'inactivo', label: t('common.inactive') },
      ],
    },
  ], [t]);

  // ✅ PERMISOS UAU - Control granular de botones
  const canCreate = hasPermission('maquinas.crear');
  const canEdit = hasPermission('maquinas.editar');
  const canDelete = hasPermission('maquinas.eliminar');
  
  // Estado local
  const [filtros, setFiltros] = useState<{ page: number; limit: number; search?: string }>({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Maquina;
  }>({
    isOpen: false,
    mode: 'create',
  });

  // Hooks de datos usando useApiQuery como en la implementación original
  const { 
    data, 
    isLoading, 
    error,
    refetch 
  } = useApiQuery(
    ['maquinas', filtros],
    () => MaquinasService.getMaquinas(filtros.page, filtros.limit, filtros.search || undefined)
  );

  // Aplicar filtrado por permisos de usuario usando useMemo
  const maquinasFiltradas = useMemo(() => {
    const items = (data as any)?.data || (data as any)?.items || [];
    if (!items.length) return [];

    if (tieneAccesoCompleto || maquinasPermitidas === null) {
      return items;
    }

    return items.filter((maquina: any) => maquinasPermitidas.includes(maquina.id));
  }, [(data as any)?.data, (data as any)?.items, maquinasPermitidas, tieneAccesoCompleto]);

  // Crear data filtrada para el componente CrudTable
  const dataFiltrada = useMemo(() => {
    return data ? {
      ...data,
      items: maquinasFiltradas,
      data: maquinasFiltradas,
      total: maquinasFiltradas.length
    } as any : {
      items: [],
      data: [],
      total: 0,
      page: 1,
      per_page: 10,
      pages: 0,
      has_next: false,
      has_prev: false,
    } as any;
  }, [data, maquinasFiltradas]);

  // ===============================
  // MANEJADORES DE EVENTOS
  // ===============================

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters, page: 1 }); // Reset a página 1 al cambiar filtros
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('common.no_permission_create'));
      return;
    }
    setFormModal({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEdit = (maquina: Maquina) => {
    setFormModal({
      isOpen: true,
      mode: 'edit',
      data: maquina,
    });
  };

  const handleDelete = async (maquina: Maquina) => {
    try {
      await MaquinasService.deleteMaquina(maquina.id);
      toast.success('Máquina eliminada exitosamente');
      refetch();
    } catch (error: any) {
      // Extraer el mensaje específico del backend
      const errorMessage = error?.message ||
                          error?.response?.data?.detail ||
                          error?.response?.data?.message ||
                          t('errors.delete_error');

      toast.error(errorMessage);
      console.error('Error eliminando máquina:', error);
    }
  };

  const handleFormSubmit = async (datos: CreateMaquinaRequest) => {
    try {
      if (formModal.mode === 'create') {
        await MaquinasService.createMaquina(datos);
        toast.success('Máquina creada exitosamente');
      } else if (formModal.data) {
        await MaquinasService.updateMaquina(formModal.data.id, datos);
        toast.success('Máquina actualizada exitosamente');
      }

      setFormModal({ isOpen: false, mode: 'create' });
      refetch();
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario máquina:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  // ===============================
  // ESTADO DE CARGA DEL FORMULARIO
  // ===============================

  const isFormLoading = false; // TODO: Implementar estados de loading para create/update

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          onClick={() => navigate('/admin')}
          className="hover:text-gray-700 transition-colors"
        >
          {t('admin.title')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.machines')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back')} a {t('admin.title')}</span>
        </button>
      </div>

      {/* Mensaje específico para operadores */}
      {usuario?.tipo_usuario === 'operador' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-blue-700">
                Mostrando solo las máquinas que tienes asignadas.
              </p>
            </div>
            <div className="text-sm text-blue-600 font-medium">
              {maquinasFiltradas.length} {t('admin.machines').toLowerCase()} disponible{maquinasFiltradas.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de carga de filtros */}
      {isLoadingFiltro && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-gray-600">
              {t('common.loading')}
            </p>
          </div>
        </div>
      )}

      {/* Tabla CRUD */}
      <CrudTable
        data={dataFiltrada}
        isLoading={isLoading || isLoadingFiltro}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.machines')}
        addButtonText={`${t('admin.add')} ${t('admin.machines').slice(0, -1)}`}
        deleteConfirmMessage={(item) => `${t('common.confirm_delete_msg')} (${item.codigo})`}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modal de formulario */}
      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={formFields}
        title={formModal.mode === 'create' ? t('common.create') : t('admin.edit_item')}
        submitText={formModal.mode === 'create' ? t('common.create') : t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default MaquinasView;
